import { createCitDepositLine } from '../types/applicationForm';
import type { CitDepositLine } from '../types/applicationForm';
import {
  registerExtractAbortController,
  unregisterExtractAbortController,
} from './demExtractAbort';

const EXTRACT_TIMEOUT_MS = 120_000;

export type CitDepositExtractLine = {
  item_no: string;
  description: string;
  qty: string;
};

export type CitDepositExtractResult = {
  invoice_no: string;
  invoice_amount: string;
  lines: CitDepositExtractLine[];
};

function getProxyKey(): string {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured for deposit invoice extraction.');
  }
  return proxyKey;
}

function digitsAmount(raw: unknown): string {
  const cleaned = String(raw ?? '')
    .replace(/,/g, '')
    .replace(/[^\d.]/g, '')
    .trim();
  if (!cleaned) return '';
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return '';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function qtyString(raw: unknown): string {
  const cleaned = String(raw ?? '')
    .replace(/,/g, '')
    .replace(/[^\d.]/g, '')
    .trim();
  if (!cleaned) return '';
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return '';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

function normalizeDepositPayload(raw: unknown): CitDepositExtractResult {
  const root = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const deposit =
    root.deposit && typeof root.deposit === 'object'
      ? (root.deposit as Record<string, unknown>)
      : root;

  const invoice_no = String(
    deposit.invoice_no ?? deposit.invoiceNo ?? deposit.document_no ?? deposit.number ?? '',
  ).trim();
  const invoice_amount = digitsAmount(
    deposit.invoice_amount ??
      deposit.invoiceAmount ??
      deposit.amount_including_gst ??
      deposit.amountIncludingGst ??
      deposit.grand_total ??
      deposit.grandTotal ??
      deposit.total_incl_gst ??
      deposit.totalInclGst ??
      deposit.amount_due ??
      deposit.amountDue ??
      deposit.total ??
      deposit.amount ??
      deposit.subtotal,
  );

  const linesRaw = Array.isArray(deposit.lines)
    ? deposit.lines
    : Array.isArray(deposit.items)
      ? deposit.items
      : [];
  const lines: CitDepositExtractLine[] = [];
  for (const row of linesRaw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const item_no = String(r.item_no ?? r.itemNo ?? r.items ?? r.sku ?? r.code ?? '').trim();
    const description = String(r.description ?? r.desc ?? '').trim();
    const qty = qtyString(r.qty ?? r.quantity ?? r.Qty);
    if (!item_no && !description && !qty) continue;
    lines.push({ item_no, description, qty });
  }

  return { invoice_no, invoice_amount, lines };
}

/** Prefer General item codes when the invoice SKU matches (case-insensitive). */
export function mapDepositExtractToCitLines(
  extracted: CitDepositExtractLine[],
  generalItemNos: string[],
): CitDepositLine[] {
  const byUpper = new Map(generalItemNos.map((code) => [code.trim().toUpperCase(), code.trim()]));
  const out: CitDepositLine[] = [];
  for (const line of extracted) {
    const raw = line.item_no.trim();
    const matched = raw ? byUpper.get(raw.toUpperCase()) : undefined;
    const item_no = matched || raw;
    if (!item_no && !line.qty) continue;
    out.push(
      createCitDepositLine({
        item_no,
        quantity: line.qty,
      }),
    );
  }
  return out.length > 0 ? out : [createCitDepositLine()];
}

export async function extractCitDepositInvoiceFromFile(
  file: File,
): Promise<CitDepositExtractResult> {
  const name = file.name.trim() || 'deposit-invoice.pdf';
  const lower = name.toLowerCase();
  if (!lower.endsWith('.pdf') && file.type !== 'application/pdf') {
    throw new Error('Deposit AI extract currently supports PDF only.');
  }

  const controller = new AbortController();
  registerExtractAbortController(controller);
  const timer = window.setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);
  try {
    const form = new FormData();
    form.append('file', file, name);
    form.append('fileName', name);
    form.append('extractType', 'deposit_invoice');

    const res = await fetch('/po-extract-api', {
      method: 'POST',
      headers: {
        'X-DEM-Email-Key': getProxyKey(),
      },
      body: form,
      signal: controller.signal,
    });
    const text = await res.text();
    if (!res.ok) {
      let message = `Deposit extract failed (${res.status})`;
      try {
        const err = JSON.parse(text) as { error?: string; detail?: string; hint?: string };
        if (err.error?.trim()) message = err.error.trim();
        if (err.detail?.trim()) message = `${message}: ${err.detail.trim().slice(0, 220)}`;
        if (err.hint?.trim()) message = `${message} (${err.hint.trim()})`;
      } catch {
        if (text.trim()) message = text.trim().slice(0, 300);
      }
      throw new Error(message);
    }
    return normalizeDepositPayload(JSON.parse(text));
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error(
        `Deposit AI extract timed out after ${Math.round(EXTRACT_TIMEOUT_MS / 1000)}s. Try again or fill manually.`,
      );
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
    unregisterExtractAbortController(controller);
  }
}
